import { scrollToFirstFormError } from "@insite/client-framework/Common/CustomerSelectors";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearMessages from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ClearMessages";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import setInitialValues from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetIsPreloadingData";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import AddressValidationModal from "@SupplyLand/Components/AddressValidationModal";
import {
    AddressValidationResult,
    AddressValidationResultModel,
    ValidationAddressModel,
} from "@SupplyLand/Services/AddressValidationService";
import { CustomState } from "@SupplyLand/Store/Components/AddressValidation/AddressValidationReducer";
import updateBillingAddressHandler from "@SupplyLand/Store/Components/AddressValidation/UpdateBillingAddressHandler";
import updateShippingAddressHandler from "@SupplyLand/Store/Components/AddressValidation/UpdateShippingAddressHandler";
import validateInputAddress from "@SupplyLand/Store/Components/AddressValidation/ValidateInputAddress";
import cloneDeep from "lodash/cloneDeep";
import React, { Component, createContext } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    preloadCheckoutShippingData: makeHandlerChainAwaitable(preloadCheckoutShippingData),
    setIsPreloadingData,
    updateCart: makeHandlerChainAwaitable(updateCart),
    setInitialValues,
    validateInputAddress,
    updateBillingAddressHandler,
    updateShippingAddressHandler,
    clearMessages,
};

const mapStateToProps = (state: CustomState) => {
    const cartId = state.pages.checkoutShipping.cartId;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    return {
        cartId,
        isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
        reviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
        location: getLocation(state),
        isUpdatingCustomer: state.pages.checkoutShipping.isUpdatingCustomer,
        shipTo: getShipToState(state, cart?.shipToId).value,
        billTo: getBillToState(state, cart?.billToId).value,
        shipToAddress: state.pages.checkoutShipping.shippingAddressFormState?.address,
        billToAddress: state.pages.checkoutShipping.billingAddressFormState?.address,
        useBillToAsShipTo: state.pages.checkoutShipping.useBillingAddress,
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        countries: getCurrentCountries(state),
        cart,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isBillingAddressUpdateRequired: state.pages.checkoutShipping.isBillingAddressUpdateRequired,
    };
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
    isRedirecting: boolean;
    forceValidation: boolean;
    addressValidationPromptRequired: boolean;
    deliveryPointValid: boolean;
    isValidationModalOpen: boolean;
    addressValidationResult: AddressValidationResult;
    inputAddress: ValidationAddressModel;
    hideSuggestedAddress: boolean;
};

export const CheckoutShippingFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
    isRedirecting: false,
    forceValidation: false,
    addressValidationPromptRequired: false,
    deliveryPointValid: false,
    isValidationModalOpen: false,
    addressValidationResult: {} as AddressValidationResult,
    inputAddress: {} as ValidationAddressModel,
    hideSuggestedAddress: false,
});

class CheckoutShippingPage extends Component<Props, State> {
    private validationResult = {} as AddressValidationResult;

    constructor(props: Props) {
        super(props);
        this.state = {
            formSubmitAttempted: false,
            validators: {},
            isRedirecting: false,
            forceValidation: false,
            addressValidationPromptRequired: false,
            deliveryPointValid: false,
            isValidationModalOpen: false,
            addressValidationResult: {} as AddressValidationResult,
            inputAddress: {} as ValidationAddressModel,
            hideSuggestedAddress: false,
        };
    }

    async componentDidMount() {
        if (!this.props.isPreloadingData) {
            const { cartId } = parseQueryString<{ cartId?: string }>(this.props.location.search);
            await this.props.preloadCheckoutShippingData({ cartId });
            this.props.setInitialValues();
        }
        this.props.setIsPreloadingData({ isPreloadingData: false });
        this.props.clearMessages();
    }

    validationModalCloseHandler = (e: any) => {
        e.preventDefault();
        this.setState({
            isValidationModalOpen: false,
        });
    };

    scrollToFirstFormError = () => {
        const firstErrorElement = document.querySelector("[aria-invalid='true']");
        if (firstErrorElement) {
            const formFieldParent = firstErrorElement.closest("[class*='FormFieldStyle']");
            if (!formFieldParent) {
                return;
            }
            const { top } = formFieldParent.getBoundingClientRect();
            window.scrollTo({
                top: top + window.pageYOffset,
                left: 0,
                behavior: "smooth",
            });
        }
    };

    acceptValidationResultHandler = (e: any) => {
        this.setState({
            isValidationModalOpen: false,
        });

        const useBillingAddressElement = document
            ?.querySelector(
                "[data-test-selector='checkoutShipping_shippingAddress'] [data-id='checkbox'] [role='checkbox']",
            )
            ?.getAttribute("aria-checked");
        const useBillingAddress = useBillingAddressElement === "true";
        if (useBillingAddress === true) {
            const clonedBillTo = cloneDeep(this.props.billToAddress);
            const { ...modifiedAddress } = clonedBillTo;
            if (modifiedAddress != null) {
                this.updateAddressFromResult(modifiedAddress, this.validationResult);
                this.props.updateBillingAddressHandler(modifiedAddress);
            }
        } else {
            const clonedShipTo = cloneDeep(this.getShipToAddress());
            const { ...modifiedAddress } = clonedShipTo;
            if (modifiedAddress != null) {
                this.updateAddressFromResult(modifiedAddress, this.validationResult);
                this.props.updateShippingAddressHandler(modifiedAddress);
            }
        }
        this.postSubmit();
    };

    updateAddressFromResult = (
        address: BillToModel | ShipToModel,
        addressValidationResult: AddressValidationResult,
    ) => {
        address.address1 = addressValidationResult.address.address1 || "";
        address.address2 = addressValidationResult.address.address2 || "";
        address.city = addressValidationResult.address.city || "";
        const country = this.props.countries?.find(c => c.abbreviation === addressValidationResult.address.country);
        address.country = country || null;
        const selectedState = country?.states?.find(s => s.abbreviation === addressValidationResult.address.state);
        address.state = selectedState || null;
        address.postalCode = addressValidationResult.address.postalCode;
        address.properties["addressClassification"] = addressValidationResult.classification;
    };

    getShipToAddress = () => {
        return this.props.shipToAddress?.oneTimeAddress ? this.props.shipToAddress : this.props.shipTo;
    };

    acceptEnteredAddressHandler = (e: any) => {
        // continue checkout without accepting address validation suggestion
        this.setState({
            isValidationModalOpen: false,
        });
        const useBillingAddressElement = document
            ?.querySelector(
                "[data-test-selector='checkoutShipping_shippingAddress'] [data-id='checkbox'] [role='checkbox']",
            )
            ?.getAttribute("aria-checked");
        const useBillingAddress = useBillingAddressElement === "true";
        if (useBillingAddress === true) {
            const clonedBillTo = cloneDeep(this.props.billToAddress);
            const { ...modifiedAddress } = clonedBillTo;
            if (modifiedAddress != null) {
                this.updateAddressFromResult(modifiedAddress, this.validationResult);
                this.props.updateBillingAddressHandler(modifiedAddress);
            }
        } else {
            const clonedShipTo = cloneDeep(this.getShipToAddress());
            const { ...modifiedAddress } = clonedShipTo;
            if (modifiedAddress != null) {
                this.updateAddressFromResult(modifiedAddress, this.validationResult);
                this.props.updateShippingAddressHandler(modifiedAddress);
            }
        }
        this.postSubmit();
    };

    postSubmit = async () => {
        this.setState({ isRedirecting: true });
        await this.props.updateCart({});
        this.setState({ formSubmitAttempted: true });
        const continueUrl = this.props.cartId
            ? `${this.props.reviewAndSubmitPageLink!.url}?cartId=${this.props.cartId}`
            : this.props.reviewAndSubmitPageLink!.url;
        this.props.history.push(continueUrl);
    };

    handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        console.log("CheckoutShippingPage > handleFormSubmit");

        const useBillingAddressElement = document
            ?.querySelector(
                "[data-test-selector='checkoutShipping_shippingAddress'] [data-id='checkbox'] [role='checkbox']",
            )
            ?.getAttribute("aria-checked");
        const useBillingAddress = useBillingAddressElement === "true";

        const validators: Validator[] = [];
        Object.keys(this.state.validators).forEach(key => {
            const validator = this.state.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));

        console.log("CheckoutShippingPage > handleFormSubmit > validatorResults", results);

        if (results.every(o => o)) {
            const userIsGuestOrUpdateRequired =
                this.props.currentUserIsGuest || this.props.isBillingAddressUpdateRequired;

            const inputAddress = !userIsGuestOrUpdateRequired
                ? this.getShipToAddress()
                : useBillingAddress
                ? this.props.billToAddress
                : this.getShipToAddress();

            const address = {} as ValidationAddressModel;
            address.address1 = inputAddress!.address1;
            address.address2 = inputAddress!.address2;
            address.address3 = inputAddress!.address3;
            address.address4 = inputAddress!.address4;
            address.city = inputAddress!.city;
            if (inputAddress!.state && inputAddress!.state!.abbreviation) {
                address.state = inputAddress!.state!.abbreviation;
            }
            address.postalCode = inputAddress!.postalCode;
            if (inputAddress!.country && inputAddress!.country!.abbreviation) {
                address.country = inputAddress!.country!.abbreviation;
            }

            address.addressType = useBillingAddress ? "billing" : "shipping";
            const needsAddressClassification =
                !inputAddress!.properties["addressClassification"] ||
                (inputAddress!.properties["addressClassification"] &&
                    inputAddress!.properties["addressClassification"].length === 0);

            if (
                this.props.cart?.fulfillmentMethod === "Ship" &&
                (userIsGuestOrUpdateRequired === true || needsAddressClassification)
            ) {
                this.setState({ addressValidationPromptRequired: false });
                this.setState({ hideSuggestedAddress: false });
                this.setState({ deliveryPointValid: false });
                this.props.validateInputAddress({
                    inputAddress: address,
                    addressFieldDisplayCollection: this.props.shipToAddressFields!,
                    onComplete: response => {
                        const { ...apiResult } = response.apiResult as AddressValidationResultModel;
                        this.validationResult = apiResult.validationResult;
                        this.setState({
                            addressValidationResult: apiResult.validationResult!,
                        });
                        this.setState({ inputAddress: address });
                        this.setState({
                            deliveryPointValid: apiResult.validationResult.dpv,
                        });

                        if (
                            apiResult.validationResult.address !== undefined &&
                            (apiResult.validationResult.dpv || apiResult.validationResult.success)
                        ) {
                            if (
                                address.address1.toUpperCase() !==
                                    (apiResult.validationResult.address.address1?.toUpperCase() || "") ||
                                address.address2.toUpperCase() !==
                                    (apiResult.validationResult.address.address2?.toUpperCase() || "") ||
                                address.city.toUpperCase() !==
                                    (apiResult.validationResult.address.city?.toUpperCase() || "") ||
                                address.state.toUpperCase() !==
                                    (apiResult.validationResult.address.state?.toUpperCase() || "") ||
                                address.postalCode !== (apiResult.validationResult.address.postalCode || "")
                            ) {
                                this.setState({ addressValidationPromptRequired: true });
                                this.setState({ isValidationModalOpen: true });
                            } else {
                                this.postSubmit();
                            }
                        }

                        if (
                            !apiResult.validationResult.dpv &&
                            !apiResult.validationResult.invalidSuiteNumber &&
                            !apiResult.validationResult.suiteRequiredButMissing
                        ) {
                            this.setState({ addressValidationPromptRequired: true });
                            this.setState({ hideSuggestedAddress: true });
                            this.setState({ isValidationModalOpen: true });
                        }
                    },
                });
            } else {
                //  Authenticated user
                this.postSubmit();
            }
        } else {
            scrollToFirstFormError();
        }
    };

    render() {
        return (
            <Page data-test-selector="checkoutShipping">
                <LoadingOverlay loading={this.props.isUpdatingCustomer}>
                    <CheckoutShippingFormContext.Provider value={this.state}>
                        <form id="checkoutShippingForm" onSubmit={this.handleFormSubmit} noValidate>
                            <Zone zoneName="Content" contentId={this.props.id} />
                        </form>
                        {this.state.addressValidationPromptRequired && (
                            <AddressValidationModal
                                isModalOpen={this.state.isValidationModalOpen}
                                onModalClose={this.validationModalCloseHandler}
                                forceValidation={this.state.forceValidation}
                                addressValidationPromptRequired={this.state.addressValidationPromptRequired}
                                onAcceptModal={this.acceptValidationResultHandler}
                                onContinueModal={this.acceptEnteredAddressHandler}
                                addressFieldDisplayCollection={this.props.shipToAddressFields!}
                                inputAddress={this.state.inputAddress}
                                address={this.state.addressValidationResult.address}
                                hideSuggestedAddress={this.state.hideSuggestedAddress}
                            />
                        )}
                    </CheckoutShippingFormContext.Provider>
                </LoadingOverlay>
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutShippingPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CheckoutShippingPage" instead of this constant.
 */
export const CheckoutShippingPageContext = "CheckoutShippingPage";
